import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { Observable, throwError, BehaviorSubject, of, EMPTY } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ToastService } from './toast.service';
import { UserProfile } from '../shared/types/user.interface';
import { LocationService } from './location.service';
import { EmployeeService } from './employee.service';

/**
 * Service for managing user profile and location-related data, providing cached API access, state streams, and helper methods to prevent duplicate requests.
 * @component
 * @selector app-profile-service
 * @example
 *   <app-profile-service [packageId]="packageId" (profileChanged)="onProfileChanged()"></app-profile-service>
 * @input string packageId - Optional package ID used to filter locations when calling getLocations or loadLocations.
 * @output Observable<UserProfile | null> userProfile$ - Emits the current user profile state; updates when profile is loaded, refreshed, set, or cleared.
 * @output Observable<any[]> locations$ - Proxy to LocationService.locations$ that emits the current list of locations.
 *
 * Purpose:
 *   ProfileService centralizes user profile and location management for the application. It wraps ApiService calls for profile and location endpoints, maintains a BehaviorSubject for synchronous access to the current user profile, and provides cached access to location details to avoid duplicate network requests.
 *
 * Injected services:
 *   - ApiService: used for HTTP interactions with profile, shop and location endpoints.
 *   - ToastService: used to display success/error notifications after important operations.
 *   - LocationService: used as the source of truth for locations, to avoid duplicate API calls and to synchronize location state between services.
 *
 * Singleton behavior:
 *   - Provided in root (singleton). Constructor injection wires up dependencies and ensures consumers share the same caches and observables.
 *
 * Key methods:
 *   - changePassword(data: {current_password: string, new_password: string}): Observable<any>
 *       Wraps API call to change the user's password.
 *   - getBusinessProfile(): Observable<any>
 *       Fetches the business/shop profile from the API (memoized until cleared).
 *   - clearBusinessProfileCache(): void
 *       Drops the memoized company/me stream (logout, login, and after shop updates).
 *   - getLocations(packageId?: string): Observable<any>
 *       Returns locations from LocationService by default; if packageId is provided, makes an API call with that filter.
 *   - loadLocations(packageId?: string): void
 *       Loads and syncs locations. If packageId is provided it clears cache and fetches fresh data; otherwise delegates to LocationService.loadLocations().
 *   - getLocationDetails(locationId: string): Observable<any>
 *       Returns location details with in-memory caching and shareReplay to prevent duplicate concurrent requests. Respects a cache duration (default 5 minutes) and manages loading state per location.
 *   - clearLocationDetailsCache(locationId?: string): void
 *       Clears cached details for a specific location or all cached location details.
 *   - updateBusinessProfile(shopId: string, data: FormData): Observable<any>
 *       Updates shop profile and shows success/error messages. On success, triggers a user-facing toast.
 *   - updateLocationDetails(locationId: string, data: any): Observable<any>
 *       Updates a location, clears relevant caches, and reloads locations to keep state consistent.
 *   - deleteLocation(locationId: string): Observable<any>
 *       Deletes a location, clears its cache, refreshes locations, and shows success/error messages.
 *   - getUserProfile(): Observable<any>
 *       Direct API call to fetch the user's profile.
 *   - getCurrentUserProfile(): UserProfile | null
 *       Synchronously returns the current profile value from the BehaviorSubject (useful for legacy sync access).
 *   - loadUserProfileIfNeeded(): void
 *       Loads the profile only if it's not already available and not currently loading; prevents duplicate parallel requests.
 *   - updateUserProfile(profileData: FormData): Observable<any>
 *       Updates profile via API and refreshes the profile on success to ensure full data is present.
 *   - setUserProfile(profile: UserProfile): void
 *       Directly sets the current profile value in the BehaviorSubject.
 *   - refreshUserProfile(): void
 *       Forces a reload of the profile from the API and updates the BehaviorSubject.
 *   - clearUserProfile(): void
 *       Clears the in-memory profile (used on logout) and resets loading state.
 *
 * Concurrency, caching and error handling:
 *   - Uses a Map-based cache for location details with timestamps and a configurable duration (5 minutes by default).
 *   - Stores in-flight observables (locationDetailsCache$) and an isLoading map to deduplicate concurrent requests for the same location.
 *   - Ensures LocationService remains the canonical source for locations; ProfileService delegates or syncs with LocationService to avoid duplicate API calls.
 *   - Uses RxJS operators (tap, catchError, shareReplay) to manage side effects, caching and error propagation.
 *
 * Lifecycle hooks:
 *   - No Angular component lifecycle hooks apply; initialization occurs via constructor injection and the service exposes methods to lazily load or refresh data as needed.
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  // Use LocationService's locations$ instead of maintaining separate state
  // This prevents duplicate API calls
  public get locations$(): Observable<any[]> {
    return this.locationService.locations$;
  }

  /**
   * Get current locations value synchronously (for backward compatibility)
   */
  getCurrentLocations(): any[] {
    return this.locationService.getCurrentLocations();
  }

  // User profile state management
  private userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  public userProfile$ = this.userProfileSubject.asObservable();

  /** Single in-flight `user/me` stream so Header + SideNav + ProfileDropdown cannot duplicate the request. */
  private profileFetch$: Observable<any> | undefined;

  // Cache for location details to prevent duplicate API calls
  private locationDetailsCache = new Map<string, { data: any; timestamp: number }>();
  private locationDetailsCache$ = new Map<string, Observable<any>>();
  private readonly LOCATION_DETAILS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private isLoadingLocationDetails = new Map<string, boolean>();

  constructor(
    private logger: LoggerService,
    private apiService: ApiService,
    private messageService: ToastService,
    private locationService: LocationService,
    private employeeService: EmployeeService
  ) {
    // Sync ProfileService locations$ with LocationService
    // This maintains backward compatibility for components using ProfileService.locations$
  }

  changePassword(data: { current_password: string; new_password: string }): Observable<any> {
    return this.apiService.patch('user/me/password', data);
  }

  private businessProfile$: Observable<any> | null = null;

  getBusinessProfile(): Observable<any> {
    if (!this.businessProfile$) {
      this.businessProfile$ = this.apiService.get('company/me').pipe(shareReplay(1));
    }
    return this.businessProfile$;
  }

  /**
   * Drop the memoized `company/me` stream so the next call hits the API.
   * Required on logout/login — shareReplay(1) would otherwise replay the
   * previous shop's name to a newly authenticated user.
   */
  clearBusinessProfileCache(): void {
    this.businessProfile$ = null;
  }

  getLocations(packageId?: string): Observable<any> {
    // Use LocationService instead of making duplicate API call
    // If packageId is provided, we still need to call the API with that param
    if (packageId) {
      const params = { package_id: packageId };
      return this.apiService.get('location/view-location-user', params);
    }
    // Otherwise, use LocationService which has caching
    return this.locationService.getLocations();
  }

  /**
   * Load locations either for a specific package (forcing a cache refresh) or via the caching LocationService loader.
   * @example
   * oadLocations('package-123')
   * undefined
   * @param {{string}} {{packageId}} - Optional package identifier; when provided clears the location cache and fetches fresh locations filtered by this package.
   * @returns {{void}} No value returned; triggers LocationService to load or update locations.
   **/
  loadLocations(packageId?: string): void {
    if (packageId) {
      // If packageId is provided, we need to fetch with that filter
      // Clear cache and fetch fresh data
      this.locationService.clearLocationCache();
      this.getLocations(packageId).subscribe({
        next: response => {
          if (response.status && response.data) {
            // Sync LocationService with the fresh data
            this.locationService.updateLocationsFromData(response.data);
          } else {
            this.locationService.updateLocationsFromData([]);
          }
        },
        error: error => {
          this.logger.error('Failed to load locations:', error);
          this.locationService.updateLocationsFromData([]);
        },
      });
    } else {
      // Use LocationService's loadLocations which has caching and prevents duplicate calls
      this.locationService.loadLocations();
    }
  }

  /**
   * Get location details with caching to prevent duplicate API calls
   */
  getLocationDetails(locationId: string): Observable<any> {
    if (!locationId) {
      return throwError(() => new Error('Location ID is required'));
    }

    const now = Date.now();
    const cached = this.locationDetailsCache.get(locationId);

    // Return cached data if still valid
    if (cached && now - cached.timestamp < this.LOCATION_DETAILS_CACHE_DURATION) {
      return of({ status: true, data: cached.data });
    }

    // If already loading this location, return the existing observable
    if (
      this.isLoadingLocationDetails.get(locationId) &&
      this.locationDetailsCache$.has(locationId)
    ) {
      return this.locationDetailsCache$.get(locationId)!;
    }

    // Mark as loading
    this.isLoadingLocationDetails.set(locationId, true);

    // Create new observable with caching
    const locationDetails$ = this.apiService.get(`location/view-location/${locationId}`).pipe(
      tap(response => {
        this.isLoadingLocationDetails.set(locationId, false);
        if (response.status && response.data) {
          this.locationDetailsCache.set(locationId, {
            data: response.data,
            timestamp: now,
          });
        }
      }),
      catchError(error => {
        this.isLoadingLocationDetails.set(locationId, false);
        this.logger.error(`Error fetching location details for ${locationId}:`, error);
        return throwError(() => error);
      }),
      shareReplay(1) // Cache the result for multiple subscribers
    );

    // Store the observable for concurrent requests
    this.locationDetailsCache$.set(locationId, locationDetails$);

    return locationDetails$;
  }

  /**
   * Clear location details cache (useful when location is updated)
   */
  clearLocationDetailsCache(locationId?: string): void {
    if (locationId) {
      this.locationDetailsCache.delete(locationId);
      this.locationDetailsCache$.delete(locationId);
      this.isLoadingLocationDetails.delete(locationId);
    } else {
      // Clear all caches
      this.locationDetailsCache.clear();
      this.locationDetailsCache$.clear();
      this.isLoadingLocationDetails.clear();
    }
  }

  updateBusinessProfile(shopId: string, data: FormData): Observable<any> {
    return this.apiService.patch(`company/${shopId}`, data).pipe(
      tap(() => {
        this.clearBusinessProfileCache();
        this.messageService.add({
          severity: 'success',
          summary: 'Changes Saved!',
          detail: 'Your changes have been saved successfully',
        });
      }),
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update business profile. Please try again.',
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Update location details for a given location ID, clear related caches, and reload locations.
   * @example
   * pdateLocationDetails(locationId, data)
   * Observable<any>
   * @param {{string}} {{locationId}} - ID of the location to update.
   * @param {{any}} {{data}} - Data payload containing the location fields to update.
   * @returns {{Observable<any>}} Return an Observable of the API response.
   **/
  updateLocationDetails(locationId: string, data: any): Observable<any> {
    return this.apiService.put(`location/edit-location/${locationId}`, data).pipe(
      tap(() => {
        // Keep current locations visible while refetching (do not clear the list to []).
        this.locationService.invalidateLocationsFetchCache();
        this.clearLocationDetailsCache(locationId);
        this.locationService.refreshLocations();
      })
    );
  }

  /**
   * Deletes a location by its ID, clears cached details for that location, refreshes the location list, and shows success or error messages.
   * @example
   * eleteLocation('abc123')
   * Observable<any>
   * @param {{string}} {{locationId}} - The unique identifier of the location to delete.
   * @returns {{Observable<any>}} Observable emitting the server response; on success it clears cache, refreshes locations and shows a success message, on error it shows an error message and propagates the error.
   **/
  deleteLocation(locationId: string): Observable<any> {
    return this.apiService.delete(`location/${locationId}`).pipe(
      tap(() => {
        // Clear location details cache for deleted location
        this.clearLocationDetailsCache(locationId);
        // Force refresh locations after successful deletion
        this.locationService.refreshLocations();
        this.messageService.add({
          severity: 'success',
          summary: 'Location Deleted!',
          detail: 'The location has been removed successfully',
        });
      }),
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error.message || 'Failed to delete location. Please try again.',
        });
        return throwError(() => error);
      })
    );
  }

  // User profile management methods
  private isLoadingProfile = false;

  getUserProfile(): Observable<any> {
    return this.apiService.getProfile();
  }

  /**
   * Gets the current user profile value synchronously
   */
  getCurrentUserProfile(): UserProfile | null {
    return this.userProfileSubject.value;
  }

  /**
   * Loads user profile only if not already loaded or currently loading.
   * This prevents multiple simultaneous API calls from different components.
   */
  loadUserProfileIfNeeded(): void {
    if (this.userProfileSubject.value !== null) {
      return;
    }

    if (!this.profileFetch$) {
      this.isLoadingProfile = true;
      this.profileFetch$ = this.getUserProfile().pipe(
        tap(response => {
          this.isLoadingProfile = false;
          if (response.status && response.data) {
            this.userProfileSubject.next(response.data);
          }
        }),
        catchError(error => {
          this.isLoadingProfile = false;
          this.profileFetch$ = undefined;
          this.logger.error('Failed to load user profile:', error);
          return EMPTY;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    this.profileFetch$.subscribe({ error: () => undefined });
  }

  updateUserProfile(profileData: FormData): Observable<any> {
    return this.apiService.updateProfile(profileData).pipe(
      tap(response => {
        if (response.status) {
          // After successful update, refresh complete profile data to ensure we have all fields
          this.employeeService.clearProfileCache();
          this.refreshUserProfile();
        }
      })
    );
  }

  setUserProfile(profile: UserProfile): void {
    this.userProfileSubject.next(profile);
  }

  /**
   * Refreshes the current user's profile by fetching the latest data, updating the internal subject, and managing the loading state.
   * @example
   * efreshUserProfile()
   * // void
   * @returns {{void}} No value is returned; method performs side effects (updates subject and loading flag).
   **/
  refreshUserProfile(): void {
    this.profileFetch$ = undefined;
    this.isLoadingProfile = true;
    this.employeeService.clearProfileCache();
    this.getUserProfile().subscribe({
      next: response => {
        this.isLoadingProfile = false;
        if (response.status && response.data) {
          this.userProfileSubject.next(response.data);
        }
      },
      error: error => {
        this.isLoadingProfile = false;
        this.logger.error('Failed to refresh user profile:', error);
      },
    });
  }

  /**
   * Clear user profile and shop cache (used on logout)
   */
  clearUserProfile(): void {
    this.userProfileSubject.next(null);
    this.isLoadingProfile = false;
    this.profileFetch$ = undefined;
    this.clearBusinessProfileCache();
  }
}
